import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import { passBusiness } from '../../../services/passApi';
import {
  Plus, QrCode, Ticket, Sparkles, Pencil, Pause, Play, Archive, ExternalLink, Loader2,
} from 'lucide-react';

const STATUS_BADGE = {
  active:   { label: 'Activa', cls: 'bg-green-50 text-green-700' },
  paused:   { label: 'Pausada', cls: 'bg-amber-50 text-amber-700' },
  archived: { label: 'Archivada', cls: 'bg-slate-100 text-slate-600' },
  draft:    { label: 'Borrador', cls: 'bg-blue-50 text-blue-700' },
};

const MODE_LABEL = {
  free_claim: 'Gratis',
  reservation_only: 'Reserva',
  deposit: 'Apartado',
  full_payment: 'Pago total',
  cover_payment: 'Cover',
  manual_lead: 'Contacto',
};

export default function PassDashboard() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    load();
  }, []);

  function load() {
    setLoading(true);
    Promise.all([
      passBusiness.listOffers().catch(() => ({ data: { offers: [] } })),
      passBusiness.listReservations().catch(() => ({ data: { reservations: [] } })),
    ])
      .then(([offersRes, reservationsRes]) => {
        setOffers(offersRes.data.offers || []);
        setReservations(reservationsRes.data.reservations || []);
      })
      .catch((err) => toast.error(err.response?.data?.detail || 'Error cargando Pass'))
      .finally(() => setLoading(false));
  }

  async function doAction(offerId, action) {
    setBusyId(offerId);
    try {
      if (action === 'pause') await passBusiness.pauseOffer(offerId);
      if (action === 'activate') await passBusiness.activateOffer(offerId);
      if (action === 'archive') {
        if (!window.confirm('¿Archivar esta oferta? No será visible para clientes.')) {
          setBusyId(null);
          return;
        }
        await passBusiness.archiveOffer(offerId);
      }
      toast.success('Cambio aplicado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aplicar el cambio');
    } finally {
      setBusyId(null);
    }
  }

  const activeCount = offers.filter((o) => o.status === 'active').length;
  const checkinsCount = reservations.filter((r) => r.checkin_status === 'checked_in').length;

  return (
    <DashboardLayout>
      <div className="px-6 py-6">
        <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-teal-600" /> Todo en Local Pass
            </h1>
            <p className="text-sm text-slate-500">Promociones con afiliados, QR y check-in.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/dashboard/pass/scanner" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold">
              <QrCode className="w-4 h-4" /> Scanner QR
            </Link>
            <Link to="/dashboard/pass/ofertas/nueva" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold">
              <Plus className="w-4 h-4" /> Nueva oferta
            </Link>
          </div>
        </header>

        <section className="grid md:grid-cols-3 gap-4 mb-8">
          <Card label="Ofertas activas" value={activeCount} />
          <Card label="Reservas / Tickets" value={reservations.length} />
          <Card label="Check-ins" value={checkinsCount} />
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-4 inline-flex items-center gap-2">
            <Ticket className="w-5 h-5 text-teal-600" /> Mis ofertas
          </h2>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : offers.length === 0 ? (
            <p className="text-slate-500 text-sm">Aún no has creado ofertas Pass.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left py-2 pr-2">Oferta</th>
                    <th className="text-left py-2 pr-2">Modo</th>
                    <th className="text-left py-2 pr-2">Estado</th>
                    <th className="text-left py-2 pr-2">Ciudad</th>
                    <th className="text-right py-2 pr-2">Marketplace</th>
                    <th className="text-right py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {offers.map((o) => {
                    const badge = STATUS_BADGE[o.status] || STATUS_BADGE.draft;
                    return (
                      <tr key={o.id}>
                        <td className="py-2 pr-2">
                          <div className="font-semibold text-slate-900">{o.title}</div>
                          <div className="text-xs text-slate-500">{o.category}</div>
                        </td>
                        <td className="py-2 pr-2 text-xs">{MODE_LABEL[o.mode] || o.mode}</td>
                        <td className="py-2 pr-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
                        </td>
                        <td className="py-2 pr-2 text-xs">{o.city || '—'}</td>
                        <td className="py-2 pr-2 text-right text-xs">{o.marketplace_visible ? '✓' : '—'}</td>
                        <td className="py-2 text-right">
                          <div className="inline-flex gap-1">
                            <ActionIcon
                              title="Editar"
                              onClick={() => navigate(`/dashboard/pass/ofertas/${o.id}`)}
                              icon={Pencil}
                            />
                            <ActionIcon
                              title="Abrir pública"
                              onClick={() => window.open(`/pass/oferta/${o.slug}`, '_blank')}
                              icon={ExternalLink}
                            />
                            {o.status === 'active' ? (
                              <ActionIcon
                                title="Pausar"
                                onClick={() => doAction(o.id, 'pause')}
                                icon={Pause}
                                busy={busyId === o.id}
                                color="amber"
                              />
                            ) : o.status === 'paused' ? (
                              <ActionIcon
                                title="Activar"
                                onClick={() => doAction(o.id, 'activate')}
                                icon={Play}
                                busy={busyId === o.id}
                                color="green"
                              />
                            ) : null}
                            {o.status !== 'archived' && (
                              <ActionIcon
                                title="Archivar"
                                onClick={() => doAction(o.id, 'archive')}
                                icon={Archive}
                                busy={busyId === o.id}
                                color="red"
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm mt-6">
          <h2 className="font-semibold text-slate-900 mb-4">Últimas reservas</h2>
          {reservations.length === 0 ? (
            <p className="text-slate-500 text-sm">Sin reservas todavía.</p>
          ) : (
            <div className="divide-y divide-slate-200">
              {reservations.slice(0, 20).map((r) => (
                <div key={r.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-semibold text-slate-900">{r.offer_title}</div>
                    <div className="text-xs text-slate-500">
                      {r.customer_name || '—'} · {r.people_count} pers · {r.status}
                    </div>
                  </div>
                  <span className="text-xs text-slate-500">
                    {new Date(r.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

function Card({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

function ActionIcon({ title, onClick, icon: Icon, busy, color }) {
  const colors = {
    amber: 'text-amber-600 hover:bg-amber-50',
    green: 'text-green-600 hover:bg-green-50',
    red:   'text-red-600 hover:bg-red-50',
  };
  const cls = colors[color] || 'text-slate-600 hover:bg-slate-100';
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={busy}
      className={`p-1.5 rounded-lg disabled:opacity-50 transition-colors ${cls}`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
    </button>
  );
}
